import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import Logger from 'utils/logger';
import type {
  TranscriptHighlight,
  LineSelection,
  NoteTag,
  NoteComment,
  PendingSelection,
  VisibilitySettings,
  NoteNotification,
} from '../types';
import {
  createNote,
  fetchNotes,
  updateNote as updateNoteApi,
  deleteNote,
  createComment,
  fetchCaseTags,
  createCaseTag,
  highlightToCreateRequest,
  noteModelToHighlight,
  noteNotificationToHighlight,
} from '../notesApi';
import type { CaseTag } from '../notesApi';
import {
  getLocalNotes,
  saveLocalNote,
  removeLocalNote,
  clearStaleLocalNotes,
} from '../notesLocalStorage';

let hlCounter = 0;
const generateId = () => `hl-${Date.now()}-${++hlCounter}`;

const isErrorResponse = (value: unknown): value is { error?: unknown } =>
  !!value && typeof value === 'object' && 'error' in value;

type UseTextHighlightsOptions = {
  caseId: string;
  sessionKey: string | null;
  userId: string | null;
  identityId: string | null;
  lineNumbersByLineId: Map<string, number>;
  pageNumbersByLineId: Map<string, number>;
  openLineIds: Set<string>;
  visibilitySettings: VisibilitySettings;
  noteNotifications?: NoteNotification[];
  userName?: string;
};

export default function useTextHighlights(
  scrollRef: React.RefObject<HTMLElement | null>,
  options: UseTextHighlightsOptions,
) {
  const { addToast } = useToasts();
  const [highlights, setHighlights] = useState<TranscriptHighlight[]>([]);
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [isHighlightOnly, setIsHighlightOnly] = useState(false);
  const pendingRef = useRef(pendingSelection);
  pendingRef.current = pendingSelection;
  const highlightsRef = useRef(highlights);
  highlightsRef.current = highlights;
  const originalNoteTextRef = useRef<string | null>(null);
  const originalVisibilityRef = useRef<'private' | 'team' | null>(null);
  const originalTagsRef = useRef<NoteTag[] | null>(null);
  const lastProcessedNotifIndexRef = useRef(-1);
  const apiReconciledNoteIdsRef = useRef<Set<string>>(new Set());
  const [isTagMode, setIsTagMode] = useState(false);
  const [availableTags, setAvailableTags] = useState<CaseTag[]>([]);
  const tagsFetchedRef = useRef(false);

  const { caseId, sessionKey, userId, identityId } = options;

  const highlightsByLine = useMemo(() => {
    const map = new Map<string, TranscriptHighlight[]>();
    for (const hl of highlights) {
      const isRemote = !!(hl.createdByUserId && identityId && hl.createdByUserId !== identityId);
      const lineSelections =
        hl.selections && hl.selections.length > 0
          ? hl.selections
          : [
              {
                lineId: hl.lineId,
                startOffset: hl.startOffset,
                endOffset: hl.endOffset,
                text: hl.text,
                lineNumberAtCreate: hl.lineNumberAtCreate,
              },
            ];

      for (let si = 0; si < lineSelections.length; si++) {
        const sel = lineSelections[si];
        let list = map.get(sel.lineId);
        if (!list) {
          list = [];
          map.set(sel.lineId, list);
        }
        list.push({
          ...hl,
          lineId: sel.lineId,
          startOffset: sel.startOffset,
          endOffset: sel.endOffset,
          text: sel.text,
          isRemote,
          showTeamBadge: si === 0,
        });
      }
    }
    for (const [key, list] of map) {
      map.set(
        key,
        list.sort((a, b) => a.startOffset - b.startOffset),
      );
    }
    return map;
  }, [highlights, identityId]);

  // Fetch persisted notes on mount when sessionKey becomes available
  useEffect(() => {
    if (!caseId || !sessionKey) return;

    // Clean up stale localStorage entries
    if (userId) {
      clearStaleLocalNotes(userId, caseId, sessionKey);
    }

    let cancelled = false;

    const loadNotes = async () => {
      try {
        const notes = await fetchNotes(caseId, sessionKey);
        if (cancelled) return;
        if (isErrorResponse(notes)) {
          Logger.ERROR('Failed to fetch notes', notes);
          // Server fetch failed — fall back to localStorage notes
          if (userId) {
            const localNotes = getLocalNotes(userId, caseId, sessionKey);
            if (localNotes.length > 0) setHighlights(localNotes);
          }
          return;
        }
        const serverNotes = (Array.isArray(notes) ? notes : [])
          .map(noteModelToHighlight)
          .filter((hl) => hl.lineId && hl.endOffset > hl.startOffset);

        if (!userId) {
          setHighlights(serverNotes);
          return;
        }

        // Merge with localStorage notes
        const localNotes = getLocalNotes(userId, caseId, sessionKey);
        const serverNoteIds = new Set(serverNotes.map((n) => n.noteId).filter(Boolean));
        const serverHighlightIds = new Set(serverNotes.map((n) => n.highlightId));

        // Remove localStorage entries that now exist on the server
        const unsyncedLocal = localNotes.filter((ln) => {
          if (ln.noteId && serverNoteIds.has(ln.noteId)) {
            removeLocalNote(userId, caseId, sessionKey, ln.highlightId);
            return false;
          }
          if (serverHighlightIds.has(ln.highlightId)) {
            removeLocalNote(userId, caseId, sessionKey, ln.highlightId);
            return false;
          }
          return true;
        });

        setHighlights([...serverNotes, ...unsyncedLocal]);
      } catch (error) {
        if (!cancelled) {
          Logger.ERROR('Failed to fetch notes', error);
          // Server fetch failed — fall back to localStorage notes
          if (userId) {
            const localNotes = getLocalNotes(userId, caseId, sessionKey);
            if (localNotes.length > 0) setHighlights(localNotes);
          }
        }
      }
    };

    loadNotes();
    return () => {
      cancelled = true;
    };
  }, [caseId, sessionKey, userId]);

  // Reset processed-notification index when session changes
  useEffect(() => {
    lastProcessedNotifIndexRef.current = -1;
  }, [sessionKey]);

  // Process incoming note notifications from SignalR
  useEffect(() => {
    const notifications = options.noteNotifications;
    if (!notifications || !notifications.length) return;
    if (!sessionKey || !identityId) return;

    const startIndex = lastProcessedNotifIndexRef.current + 1;
    if (startIndex >= notifications.length) return;

    const pending = notifications.slice(startIndex);
    if (!pending.length) return;

    setHighlights((prev) => {
      let next = [...prev];
      for (const notif of pending) {
        if (notif.sessionKey !== sessionKey) continue;

        // Normalize payload — support both PascalCase (existing events) and
        // camelCase (new NoteUpdated format)
        const p = notif.payload;
        const noteType = p.Type ?? p.type;
        const noteId = p.NoteId ?? p.noteId ?? '';
        const createdByUserId = p.CreatedByUserId ?? p.createdByUserId ?? '';
        const highlightId = `note-${noteId}`;
        const isOwnNotification = createdByUserId === identityId;

        // Skip own SignalR notifications for notes already reconciled from API response.
        // This includes NoteDeleted: when the creator toggles visibility to private the
        // server broadcasts NoteDeleted so other users remove it, but the creator's state
        // is already correct from the PATCH response — their private note must stay.
        if (isOwnNotification && apiReconciledNoteIdsRef.current.has(noteId)) {
          if (noteType === 'NoteCommentCreated') {
            // Only skip if this specific comment already exists locally
            const existing = next.find((h) => h.noteId === noteId);
            const commentId = p.CommentId ?? null;
            const commentText = p.CommentText ?? null;
            if (commentId && existing?.comments?.some((c) => c.commentId === commentId)) {
              continue;
            }
            if (commentText && existing?.comments?.some((c) => c.commentText === commentText)) {
              continue;
            }
          } else {
            continue; // NoteCreated, NoteUpdated, or NoteDeleted — already applied from API
          }
        }

        // For own NoteCommentCreated, always deduplicate even if noteId isn't reconciled.
        // The API response from submitComment may have already appended this comment.
        if (isOwnNotification && noteType === 'NoteCommentCreated') {
          const existing = next.find((h) => h.noteId === noteId);
          const commentId = p.CommentId ?? null;
          const commentText = p.CommentText ?? null;
          if (
            existing?.comments?.some(
              (c) =>
                (commentId && c.commentId === commentId) ||
                (commentText && c.commentText === commentText),
            )
          ) {
            continue;
          }
        }

        // NoteCommentCreated: append comment to existing highlight
        if (noteType === 'NoteCommentCreated') {
          const idx = next.findIndex((h) => h.noteId === noteId || h.highlightId === highlightId);
          if (idx >= 0) {
            const existing = next[idx];
            const newComment: NoteComment = {
              commentId: p.CommentId ?? '',
              commentText: p.CommentText ?? '',
              createdAtUtc: p.CommentCreatedAtUtc ?? new Date().toISOString(),
              createdBy: {
                id: createdByUserId,
                name: p.CreatedByUsername ?? p.createdByUsername ?? '',
                teamId: p.CreatedByTeamId ?? p.createdByTeamId,
                teamName: p.CreatedByTeamName ?? p.createdByTeamName,
              },
            };
            if (!(existing.comments ?? []).some((c) => c.commentId === newComment.commentId)) {
              next[idx] = {
                ...existing,
                isCommentsVisible: true,
                hasComments: true,
                comments: [...(existing.comments ?? []), newComment],
              };
            }
          }
          continue;
        }

        // NoteDeleted: remove regardless of visibility
        if (noteType === 'NoteDeleted') {
          next = next.filter((h) => h.noteId !== noteId && h.highlightId !== highlightId);
          continue;
        }

        // For create/update, process all self-authored notifications so the local
        // optimistic note can reconcile with the server payload. Remote traffic is
        // still limited to team-visible notes.
        const visibility = p.Visibility ?? p.visibility;
        if (!isOwnNotification && visibility !== 'team') continue;

        switch (noteType) {
          case 'NoteCreated': {
            const incoming = noteNotificationToHighlight(notif.payload);
            const firstSel = p.Selections?.[0];
            const dedupLineId = firstSel ? firstSel.LineId : p.LineId;
            const dedupStart = firstSel ? firstSel.Start : (p.Selection?.Start ?? -1);
            const dedupEnd = firstSel ? firstSel.End : (p.Selection?.End ?? -1);
            const existingIndex = next.findIndex(
              (h) =>
                h.noteId === noteId ||
                h.highlightId === highlightId ||
                (h.lineId === dedupLineId &&
                  h.startOffset === dedupStart &&
                  h.endOffset === dedupEnd),
            );
            if (existingIndex >= 0) {
              const existing = next[existingIndex];
              next[existingIndex] = {
                ...incoming,
                highlightId: existing.highlightId,
                isCommentsVisible: true,
                comments: existing.comments ?? incoming.comments,
                hasComments: existing.hasComments ?? incoming.hasComments,
                isLocalOnly: undefined,
                savedAt: undefined,
              };
            } else {
              next.push(incoming);
            }
            break;
          }
          case 'NoteUpdated': {
            const idx = next.findIndex((h) => h.noteId === noteId || h.highlightId === highlightId);
            if (idx >= 0) {
              const existing = next[idx];
              const noteText = p.NoteText ?? p.noteText;
              const tags = p.Tags ?? p.tags;
              const updated: TranscriptHighlight = {
                ...existing,
                note: noteText ?? existing.note,
                isCommentsVisible: true,
                tags: tags?.length
                  ? tags.map((t: { Id?: string; id?: string; Name?: string; name?: string }) => ({
                      id: t.Id ?? t.id ?? '',
                      name: t.Name ?? t.name ?? '',
                    }))
                  : existing.tags,
                visibility: visibility === 'team' ? 'team' : 'private',
              };

              // camelCase selections (new format)
              const camelSelections = p.selections;
              // PascalCase selections (old format)
              const pascalSelections = p.Selections;

              if (camelSelections && camelSelections.length > 0) {
                const sels: LineSelection[] = camelSelections
                  .filter((s: { type?: string }) => s.type !== 'Line')
                  .map(
                    (s: {
                      lineId: string;
                      lineNumberAtCreate?: number;
                      pageNumber?: number;
                      start: number;
                      end: number;
                      selectedText?: string;
                    }) => ({
                      lineId: s.lineId,
                      lineNumberAtCreate: s.lineNumberAtCreate,
                      pageNumber: s.pageNumber,
                      startOffset: s.start,
                      endOffset: s.end,
                      text: s.selectedText ?? '',
                    }),
                  );
                if (sels.length > 0) {
                  updated.lineId = sels[0].lineId;
                  updated.startOffset = sels[0].startOffset;
                  updated.endOffset = sels[0].endOffset;
                  updated.text = sels.map((s) => s.text).join(' ');
                  updated.selections = sels.length > 1 ? sels : undefined;
                }
              } else if (pascalSelections && pascalSelections.length > 0) {
                const sels: LineSelection[] = pascalSelections.map((s) => ({
                  lineId: s.LineId,
                  lineNumberAtCreate: s.LineNumberAtCreate,
                  pageNumber: s.PageNumber,
                  startOffset: s.Start,
                  endOffset: s.End,
                  text: s.SelectedText ?? '',
                }));
                updated.lineId = sels[0].lineId;
                updated.startOffset = sels[0].startOffset;
                updated.endOffset = sels[0].endOffset;
                updated.text = sels.map((s) => s.text).join(' ');
                updated.selections = sels.length > 1 ? sels : undefined;
              } else if (p.Selection) {
                updated.startOffset = p.Selection.Start;
                updated.endOffset = p.Selection.End;
                updated.text = p.Selection.SelectedText ?? existing.text;
              }

              next[idx] = updated;
            } else {
              next.push(noteNotificationToHighlight(notif.payload));
            }
            break;
          }
        }
      }
      return next;
    });

    lastProcessedNotifIndexRef.current = notifications.length - 1;
  }, [options.noteNotifications, sessionKey, identityId]);

  const captureSelection = useCallback((): boolean => {
    const selection = document.getSelection();
    if (!selection || selection.isCollapsed) return false;

    const range = selection.getRangeAt(0);
    if (!range || range.collapsed) return false;

    const container = scrollRef.current;
    if (!container) return false;

    // Walk up from both ends of the selection to find the enclosing line element.
    // Only allow highlighting within a single transcript line's text column.
    const startLine = (
      range.startContainer instanceof HTMLElement
        ? range.startContainer
        : range.startContainer.parentElement
    )?.closest('[data-line-id]');
    const endLine = (
      range.endContainer instanceof HTMLElement
        ? range.endContainer
        : range.endContainer.parentElement
    )?.closest('[data-line-id]');

    // Discard if selection is outside transcript lines
    if (!startLine || !endLine) return false;

    if (startLine === endLine) {
      // --- Single-line selection ---
      const textSpan = startLine.querySelector('.real-time__text');
      if (!textSpan) return false;
      if (!textSpan.contains(range.startContainer) || !textSpan.contains(range.endContainer))
        return false;

      const lineId = startLine.getAttribute('data-line-id');
      if (!lineId) return false;

      if (options.openLineIds.has(lineId)) {
        selection.removeAllRanges();
        addToast(
          'This line is still being transcribed. Wait for it to finalize before adding notes, highlights, or tags.',
          { appearance: 'warning', autoDismiss: true },
        );
        return false;
      }

      const preRange = document.createRange();
      preRange.setStart(textSpan, 0);
      preRange.setEnd(range.startContainer, range.startOffset);
      const startOffset = preRange.toString().length;

      const selectedText = range.toString();
      if (!selectedText.length) return false;

      const rect = range.getBoundingClientRect();
      setPendingSelection({
        ranges: [
          { lineId, startOffset, endOffset: startOffset + selectedText.length, text: selectedText },
        ],
        anchorRect: rect,
      });
      return true;
    }

    // --- Multi-line selection ---
    // Collect all [data-line-id] elements between startLine and endLine (inclusive) in DOM order
    const allLineEls = Array.from(container.querySelectorAll('[data-line-id]'));
    const startIdx = allLineEls.indexOf(startLine as Element);
    const endIdx = allLineEls.indexOf(endLine as Element);
    if (startIdx === -1 || endIdx === -1) return false;

    const lo = Math.min(startIdx, endIdx);
    const hi = Math.max(startIdx, endIdx);
    const spannedEls = allLineEls.slice(lo, hi + 1);

    // Validate: none of the spanned lines are open
    for (const el of spannedEls) {
      const lid = el.getAttribute('data-line-id');
      if (lid && options.openLineIds.has(lid)) {
        selection.removeAllRanges();
        addToast(
          'Your selection includes a line still being transcribed. Wait for it to finalize before adding notes, highlights, or tags.',
          { appearance: 'warning', autoDismiss: true },
        );
        return false;
      }
    }

    const ranges: PendingSelection['ranges'] = [];
    for (let i = 0; i < spannedEls.length; i++) {
      const el = spannedEls[i];
      const lid = el.getAttribute('data-line-id');
      if (!lid) continue;
      const textSpan = el.querySelector('.real-time__text');
      if (!textSpan) continue;
      const fullText = textSpan.textContent ?? '';

      if (i === 0) {
        // First line: from selection start to end of text
        const preRange = document.createRange();
        preRange.setStart(textSpan, 0);
        preRange.setEnd(range.startContainer, range.startOffset);
        const sOffset = preRange.toString().length;
        ranges.push({
          lineId: lid,
          startOffset: sOffset,
          endOffset: fullText.length,
          text: fullText.slice(sOffset),
        });
      } else if (i === spannedEls.length - 1) {
        // Last line: from start of text to selection end
        const preRange = document.createRange();
        preRange.setStart(textSpan, 0);
        preRange.setEnd(range.endContainer, range.endOffset);
        const eOffset = preRange.toString().length;
        ranges.push({
          lineId: lid,
          startOffset: 0,
          endOffset: eOffset,
          text: fullText.slice(0, eOffset),
        });
      } else {
        // Middle lines: full text
        ranges.push({ lineId: lid, startOffset: 0, endOffset: fullText.length, text: fullText });
      }
    }

    if (ranges.length === 0 || ranges.every((r) => !r.text.length)) return false;

    const rect = range.getBoundingClientRect();
    setPendingSelection({ ranges, anchorRect: rect });
    return true;
  }, [scrollRef, options.openLineIds, addToast]);

  // Helper to persist a highlight to the backend via POST.
  // On failure: keeps the highlight in state as local-only and saves to localStorage.
  const persistHighlight = useCallback(
    async (hl: TranscriptHighlight) => {
      if (!options.caseId || !options.sessionKey) {
        // No userId means we can't use localStorage — fall back to rollback
        if (!options.userId) {
          setHighlights((prev) => prev.filter((h) => h.highlightId !== hl.highlightId));
          addToast(hl.note ? 'Failed to create the note.' : 'Failed to create the highlight.', {
            appearance: 'error',
            autoDismiss: true,
          });
          return;
        }
        // Save locally
        setHighlights((prev) =>
          prev.map((h) => (h.highlightId === hl.highlightId ? { ...h, isLocalOnly: true } : h)),
        );
        saveLocalNote(options.userId, options.caseId, options.sessionKey ?? '', hl);
        if (hl.visibility === 'team') {
          addToast('Note saved locally but not shared with the team.', {
            appearance: 'warning',
            autoDismiss: true,
          });
        }
        return;
      }

      const markAsLocalOnly = () => {
        setHighlights((prev) =>
          prev.map((h) => (h.highlightId === hl.highlightId ? { ...h, isLocalOnly: true } : h)),
        );
        if (options.userId) {
          saveLocalNote(options.userId, options.caseId, options.sessionKey!, hl);
        }
        if (hl.visibility === 'team') {
          addToast('Note saved locally but not shared with the team.', {
            appearance: 'warning',
            autoDismiss: true,
          });
        }
      };

      try {
        const created = await createNote(
          options.caseId,
          options.sessionKey,
          highlightToCreateRequest(hl),
        );
        if (created && !isErrorResponse(created) && created.id) {
          // Success — apply full server response and remove from localStorage
          const serverHighlight = noteModelToHighlight(created);
          setHighlights((prev) =>
            prev.map((h) =>
              h.highlightId === hl.highlightId
                ? {
                    ...serverHighlight,
                    highlightId: hl.highlightId,
                    isLocalOnly: undefined,
                    savedAt: undefined,
                    isCommentsVisible: true,
                  }
                : h,
            ),
          );
          apiReconciledNoteIdsRef.current.add(created.id);
          if (options.userId) {
            removeLocalNote(options.userId, options.caseId, options.sessionKey, hl.highlightId);
          }
        } else {
          Logger.ERROR('Failed to create note — saving locally', created);
          markAsLocalOnly();
        }
      } catch (error) {
        Logger.ERROR('Failed to create note — saving locally', error);
        markAsLocalOnly();
      }
    },
    [options.caseId, options.sessionKey, options.userId, addToast],
  );

  const commitHighlight = useCallback(
    (withNote: boolean) => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;

      const now = Date.now();
      const visibility = withNote
        ? options.visibilitySettings.note
        : options.visibilitySettings.highlight;

      const selections: LineSelection[] = pending.ranges.map((r) => ({
        lineId: r.lineId,
        lineNumberAtCreate: options.lineNumbersByLineId.get(r.lineId),
        pageNumber: options.pageNumbersByLineId.get(r.lineId),
        startOffset: r.startOffset,
        endOffset: r.endOffset,
        text: r.text,
      }));

      const newHighlight: TranscriptHighlight = {
        highlightId: generateId(),
        lineId: selections[0].lineId,
        lineNumberAtCreate: selections[0].lineNumberAtCreate,
        pageNumber: selections[0].pageNumber,
        startOffset: selections[0].startOffset,
        endOffset: selections[0].endOffset,
        text: pending.ranges.map((r) => r.text).join(' '),
        note: '',
        visibility,
        createdAt: now,
        isCommentsVisible: !withNote,
        selections: selections.length > 1 ? selections : undefined,
      };

      setHighlights((prev) => [...prev, newHighlight]);
      setPendingSelection(null);
      document.getSelection()?.removeAllRanges();

      if (withNote) {
        setIsEditingExisting(false);
        setIsHighlightOnly(false);
        setActiveNoteId(newHighlight.highlightId);
      } else {
        persistHighlight(newHighlight);
      }
    },
    [options.lineNumbersByLineId, options.pageNumbersByLineId, options.visibilitySettings, persistHighlight],
  );

  const loadTags = useCallback(async () => {
    if (tagsFetchedRef.current || !options.caseId) return;
    tagsFetchedRef.current = true;
    try {
      const tags = await fetchCaseTags(options.caseId);
      if (!isErrorResponse(tags) && Array.isArray(tags)) {
        setAvailableTags(tags);
      } else {
        Logger.ERROR('Failed to fetch case tags', tags);
      }
    } catch (error) {
      Logger.ERROR('Failed to fetch case tags', error);
    }
  }, [options.caseId]);

  const commitTag = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;

    const now = Date.now();

    const selections: LineSelection[] = pending.ranges.map((r) => ({
      lineId: r.lineId,
      lineNumberAtCreate: options.lineNumbersByLineId.get(r.lineId),
      pageNumber: options.pageNumbersByLineId.get(r.lineId),
      startOffset: r.startOffset,
      endOffset: r.endOffset,
      text: r.text,
    }));

    const newHighlight: TranscriptHighlight = {
      highlightId: generateId(),
      lineId: selections[0].lineId,
      lineNumberAtCreate: selections[0].lineNumberAtCreate,
      pageNumber: selections[0].pageNumber,
      startOffset: selections[0].startOffset,
      endOffset: selections[0].endOffset,
      text: pending.ranges.map((r) => r.text).join(' '),
      note: '',
      tags: [],
      visibility: options.visibilitySettings.tag,
      createdAt: now,
      selections: selections.length > 1 ? selections : undefined,
    };

    setHighlights((prev) => [...prev, newHighlight]);
    setPendingSelection(null);
    document.getSelection()?.removeAllRanges();

    setIsEditingExisting(false);
    setIsHighlightOnly(false);
    setIsTagMode(true);
    originalTagsRef.current = [];
    setActiveNoteId(newHighlight.highlightId);
    loadTags();
  }, [options.lineNumbersByLineId, options.pageNumbersByLineId, options.visibilitySettings, loadTags]);

  const updateNote = useCallback((id: string, text: string) => {
    setHighlights((prev) => prev.map((hl) => (hl.highlightId === id ? { ...hl, note: text } : hl)));
  }, []);

  const updateTags = useCallback((id: string, tags: NoteTag[]) => {
    setHighlights((prev) => prev.map((hl) => (hl.highlightId === id ? { ...hl, tags } : hl)));
  }, []);

  const createTag = useCallback(
    async (name: string): Promise<NoteTag | null> => {
      if (!options.caseId) return null;
      try {
        const created = await createCaseTag(options.caseId, name);
        if (created && !isErrorResponse(created) && created.id) {
          setAvailableTags((prev) => [...prev, created]);
          return { id: created.id, name: created.name };
        }
        Logger.ERROR('Failed to create tag', created);
        return null;
      } catch (error) {
        Logger.ERROR('Failed to create tag', error);
        return null;
      }
    },
    [options.caseId],
  );

  const toggleVisibility = useCallback(
    (id: string) => {
      const hl = highlightsRef.current.find((h) => h.highlightId === id);
      if (!hl || hl.isLocalOnly) return;

      const newVisibility = hl.visibility === 'team' ? 'private' : 'team';
      const previousVisibility = hl.visibility;

      // Optimistically update local state
      setHighlights((prev) =>
        prev.map((h) => (h.highlightId === id ? { ...h, visibility: newVisibility } : h)),
      );

      // If persisted on backend, immediately PATCH
      if (hl.noteId && !hl.isLocalOnly && options.caseId && options.sessionKey) {
        updateNoteApi(options.caseId, options.sessionKey, hl.noteId, {
          noteText: hl.note,
          visibility: newVisibility,
          tags: hl.tags,
        })
          .then((result) => {
            if (!result || isErrorResponse(result)) {
              throw new Error('Toggle visibility returned error');
            }
            // Update the originalVisibilityRef so dismissPopup won't re-PATCH for this change
            originalVisibilityRef.current = newVisibility;
            if (hl.noteId) apiReconciledNoteIdsRef.current.add(hl.noteId);
          })
          .catch((error) => {
            Logger.ERROR('Failed to toggle visibility', error);
            // Revert optimistic update
            setHighlights((prev) =>
              prev.map((h) =>
                h.highlightId === id ? { ...h, visibility: previousVisibility } : h,
              ),
            );
            addToast('Failed to update visibility.', {
              appearance: 'error',
              autoDismiss: true,
            });
          });
      } else {
        // Not yet persisted — just update the ref so dismissPopup uses the new value
        originalVisibilityRef.current = newVisibility;
      }
    },
    [options.caseId, options.sessionKey, addToast],
  );

  const submitComment = useCallback(
    async (highlightId: string, commentText: string): Promise<boolean> => {
      const hl = highlightsRef.current.find((h) => h.highlightId === highlightId);
      if (!hl?.noteId || !options.caseId || !options.sessionKey) return false;
      try {
        const result = await createComment(
          options.caseId,
          options.sessionKey,
          hl.noteId,
          commentText,
        );
        if (result && !isErrorResponse(result)) {
          const newComment: NoteComment = {
            commentId: result.id ?? '',
            commentText: result.commentText ?? commentText,
            createdAtUtc: result.createdAtUtc ?? new Date().toISOString(),
            createdBy: result.createdBy ?? {
              id: options.identityId ?? '',
              name: options.userName ?? '',
            },
          };
          setHighlights((prev) =>
            prev.map((h) => {
              if (h.highlightId !== highlightId) return h;
              // Guard against duplicate if SignalR already delivered this comment
              if ((h.comments ?? []).some((c) => c.commentId === newComment.commentId)) return h;
              return {
                ...h,
                hasComments: true,
                isCommentsVisible: true,
                comments: [...(h.comments ?? []), newComment],
              };
            }),
          );
          if (hl?.noteId) apiReconciledNoteIdsRef.current.add(hl.noteId);
          return true;
        }
        addToast('Failed to submit reply.', { appearance: 'error', autoDismiss: true });
        return false;
      } catch (error) {
        Logger.ERROR('Failed to submit comment', error);
        addToast('Failed to submit reply.', { appearance: 'error', autoDismiss: true });
        return false;
      }
    },
    [options.caseId, options.sessionKey, options.identityId, options.userName, addToast],
  );

  const removeHighlight = useCallback(
    (highlightId: string) => {
      const hl = highlightsRef.current.find((h) => h.highlightId === highlightId);

      setHighlights((prev) => prev.filter((h) => h.highlightId !== highlightId));
      setActiveNoteId(null);

      // Remove from localStorage
      if (options.userId && options.caseId && options.sessionKey) {
        removeLocalNote(options.userId, options.caseId, options.sessionKey, highlightId);
      }

      // Delete from backend (skip for local-only notes that were never persisted)
      if (hl?.noteId && !hl.isLocalOnly && options.caseId && options.sessionKey) {
        deleteNote(options.caseId, options.sessionKey, hl.noteId).catch((error) =>
          Logger.ERROR('Failed to delete note', error),
        );
      }
    },
    [options.caseId, options.sessionKey, options.userId],
  );

  const openNoteForHighlight = useCallback(
    (highlightId: string) => {
      const hl = highlightsRef.current.find((h) => h.highlightId === highlightId);
      originalNoteTextRef.current = hl?.note ?? '';
      originalVisibilityRef.current = hl?.visibility ?? 'private';
      const hasTags = hl?.tags && hl.tags.length > 0;
      originalTagsRef.current = hl?.tags ? [...hl.tags] : [];
      setPendingSelection(null);
      setIsEditingExisting(true);
      setIsHighlightOnly(false);
      setIsTagMode(!!hasTags);
      setActiveNoteId(highlightId);
      if (hasTags) loadTags();
    },
    [loadTags],
  );

  const dismissPopup = useCallback(() => {
    // Persist to backend before dismissing
    if (activeNoteId && options.caseId && options.sessionKey) {
      const hl = highlightsRef.current.find((h) => h.highlightId === activeNoteId);
      if (hl) {
        if (hl.noteId && !hl.isLocalOnly) {
          // Existing backend note — only PATCH if note text, visibility, or tags actually changed
          const noteChanged = (hl.note ?? '') !== (originalNoteTextRef.current ?? '');
          const visibilityChanged =
            (hl.visibility ?? 'private') !== (originalVisibilityRef.current ?? 'private');
          const tagsChanged =
            JSON.stringify(hl.tags ?? []) !== JSON.stringify(originalTagsRef.current ?? []);
          if (noteChanged || visibilityChanged || tagsChanged) {
            const hlId = hl.highlightId;
            const currentNote = hl.note;
            const currentVisibility = hl.visibility || 'private';
            const currentTags = hl.tags;
            updateNoteApi(options.caseId, options.sessionKey, hl.noteId, {
              noteText: currentNote,
              visibility: currentVisibility,
              tags: currentTags,
            })
              .then((result) => {
                if (result && !isErrorResponse(result)) {
                  // Success — apply full server response
                  const serverHighlight = noteModelToHighlight(result);
                  setHighlights((prev) =>
                    prev.map((h) =>
                      h.highlightId === hlId
                        ? {
                            ...serverHighlight,
                            highlightId: hlId,
                            isLocalOnly: undefined,
                            savedAt: undefined,
                            isCommentsVisible: true,
                          }
                        : h,
                    ),
                  );
                  apiReconciledNoteIdsRef.current.add(result.id ?? (result as any).noteId);
                  if (options.userId) {
                    removeLocalNote(options.userId, options.caseId, options.sessionKey!, hlId);
                  }
                } else {
                  throw new Error('Update returned error response');
                }
              })
              .catch((error) => {
                Logger.ERROR('Failed to update note — saving locally', error);
                // Keep user's edits, mark as local-only
                setHighlights((prev) =>
                  prev.map((h) => (h.highlightId === hlId ? { ...h, isLocalOnly: true } : h)),
                );
                if (options.userId) {
                  const current = highlightsRef.current.find((h) => h.highlightId === hlId);
                  if (current) {
                    saveLocalNote(options.userId, options.caseId, options.sessionKey!, current);
                  }
                }
                if (currentVisibility === 'team') {
                  addToast('Note saved locally but not shared with the team.', {
                    appearance: 'warning',
                    autoDismiss: true,
                  });
                }
              });
          }
        } else {
          // New highlight or local-only note — POST now (includes note text and visibility)
          const hasContent = !!(hl.note || (hl.tags && hl.tags.length > 0));
          if (hl.isLocalOnly || hasContent) {
            // Local-only retry or user entered content — persist
            persistHighlight(hl);
          } else {
            // User dismissed without entering anything — discard the empty highlight
            setHighlights((prev) => prev.filter((h) => h.highlightId !== hl.highlightId));
          }
        }
      }
    }

    originalNoteTextRef.current = null;
    originalVisibilityRef.current = null;
    originalTagsRef.current = null;
    setPendingSelection(null);
    setActiveNoteId(null);
    setIsEditingExisting(false);
    setIsHighlightOnly(false);
    setIsTagMode(false);
  }, [
    activeNoteId,
    options.caseId,
    options.sessionKey,
    options.userId,
    persistHighlight,
    addToast,
  ]);

  const isRemoteNote = useMemo(() => {
    if (!activeNoteId || !identityId) return false;
    const hl = highlights.find((h) => h.highlightId === activeNoteId);
    return !!(hl?.createdByUserId && hl.createdByUserId !== identityId);
  }, [activeNoteId, highlights, identityId]);

  return {
    highlights,
    pendingSelection,
    activeNoteId,
    isEditingExisting,
    isHighlightOnly,
    isTagMode,
    isRemoteNote,
    availableTags,
    highlightsByLine,
    captureSelection,
    commitHighlight,
    commitTag,
    updateNote,
    updateTags,
    createTag,
    removeHighlight,
    openNoteForHighlight,
    dismissPopup,
    setActiveNoteId,
    toggleVisibility,
    submitComment,
  };
}
